import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, CheckCircle, XCircle, Clock, LogIn, Loader2, Lock, Copy, Download, KeyRound, RefreshCw, Eye, EyeOff, MailCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { useAuth, canModule } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { CreateMerchantForm } from './CreateMerchantForm';
import { useAssignableUsers } from '@/components/merchant/OwnerSelect';
import { buildMerchantImpersonateUrl } from './impersonate';
import { EmailVerificationCampaign } from './EmailVerificationCampaign';

interface Merchant {
  id: number;
  companyName: string;
  brandName?: string;
  email: string;
  mobile: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | 'ACTIVE' | 'INACTIVE';
  billingMode: 'PREPAID' | 'POSTPAID';
  walletBalance: string | number;
  serviceType: string;
  createdAt: string;
  lastLoginAt?: string | null;
  state?: string | null;
  salesUserId?: number | null;
  csUserId?: number | null;
  salesUserName?: string | null;
  csUserName?: string | null;
  emailVerified?: boolean;
  mobileVerified?: boolean;
  onboardingStatus?: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED' | 'COMPLETED';
}

/** Yes/No pill. Deliberately reads as words — "Verified: No" is scanned faster than an empty cell. */
function YesNo({ value }: { value?: boolean }) {
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${value ? 'bg-success/10 text-success' : 'bg-slate-100 text-slate-500'}`}>
      {value ? 'Yes' : 'No'}
    </span>
  );
}

/** Format a login timestamp in IST, or "Never" when the merchant has not logged in yet. */
function fmtLastLogin(v?: string | null): string {
  if (!v) return 'Never';
  const d = new Date(v);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Format the registration date (day-month-year in IST). */
function fmtRegistered(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
}

const statusStyles: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-success/10 text-success',
  ACTIVE: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  SUSPENDED: 'bg-orange-50 text-orange-700',
  INACTIVE: 'bg-slate-100 text-slate-500',
};

// Gmail-style pagination footer
function PaginationFooter({ page, pageSize, total, onPage, onPageSize }: {
  page: number; pageSize: number; total: number;
  onPage: (p: number) => void; onPageSize: (n: number) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-end gap-4 px-4 py-2 text-sm text-slate-600 border-t border-slate-200 bg-slate-50">
      <div className="flex items-center gap-2">
        Rows per page:
        <select value={pageSize} onChange={(e) => { onPageSize(Number(e.target.value)); onPage(1); }}
          className="px-2 py-1 border rounded outline-none">
          {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="tabular-nums">{from}–{to} of {total}</div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(1)} disabled={page <= 1}
          className="px-2 py-1 rounded hover:bg-slate-200 disabled:opacity-40">«</button>
        <button onClick={() => onPage(page - 1)} disabled={page <= 1}
          className="px-2 py-1 rounded hover:bg-slate-200 disabled:opacity-40">‹</button>
        <button onClick={() => onPage(page + 1)} disabled={page >= lastPage}
          className="px-2 py-1 rounded hover:bg-slate-200 disabled:opacity-40">›</button>
        <button onClick={() => onPage(lastPage)} disabled={page >= lastPage}
          className="px-2 py-1 rounded hover:bg-slate-200 disabled:opacity-40">»</button>
      </div>
    </div>
  );
}

// Generate a readable strong password (no ambiguous chars) for an admin-initiated reset.
function genPassword(len = 12): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%';
  const buf = new Uint32Array(len);
  (window.crypto ?? (window as any).msCrypto).getRandomValues(buf);
  let out = '';
  for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
  return out;
}

export function MerchantsListPage() {
  const { user } = useAuth();
  const isAdmin = user?.userType === 'SUPER_ADMIN' || user?.userType === 'ADMIN';
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [tab, setTab] = useState<'all' | 'pending'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loginAsTarget, setLoginAsTarget] = useState<Merchant | null>(null);
  const [adminPwd, setAdminPwd] = useState('');
  const [loginAsBusy, setLoginAsBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState<Merchant | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(true);
  const [resetBusy, setResetBusy] = useState(false);
  // Ownership filters — '' = any, '-1' = unassigned, otherwise a user id.
  const [salesUserId, setSalesUserId] = useState<string>('');
  const [csUserId, setCsUserId] = useState<string>('');
  // '' = any, 'true' / 'false' = filter on it.
  const [emailVerified, setEmailVerified] = useState<string>('');
  const [showCampaign, setShowCampaign] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: assignableUsers } = useAssignableUsers();

  const { data: merchants, isLoading } = useQuery({
    queryKey: ['merchants', { search, status, tab, salesUserId, csUserId, emailVerified }],
    queryFn: async () => {
      if (tab === 'pending') {
        return (await api.get<Merchant[]>('/merchants/pending-approvals')).data;
      }
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (status !== 'all') params.status = status;
      // '-1' = unassigned, so ops can find accounts with no owner yet.
      if (salesUserId) params.salesUserId = String(salesUserId);
      if (csUserId) params.csUserId = String(csUserId);
      if (emailVerified) params.emailVerified = emailVerified;
      return (await api.get<Merchant[]>('/merchants', { params })).data;
    },
  });

  const approve = useMutation({
    mutationFn: async (id: number) => api.post(`/merchants/${id}/approve`, { billingMode: 'PREPAID', remittanceTatDays: 5 }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchants'] }); toast.success('Merchant approved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Approval failed'),
  });
  const reject = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => api.post(`/merchants/${id}/reject`, { reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchants'] }); toast.success('Merchant rejected'); },
  });
  const resendVerify = useMutation({
    mutationFn: async (id: number) => (await api.post(`/admin/merchants/${id}/email-verification/resend`)).data,
    onSuccess: (r: any) => toast.success(r?.alreadyVerified ? 'Already verified' : `Verification link sent to ${r?.email}`),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not send'),
  });

  const doImpersonate = async () => {
    if (!loginAsTarget || !adminPwd) return;
    setLoginAsBusy(true);
    try {
      const r = await api.post(`/admin/merchants/${loginAsTarget.id}/impersonate`, { adminPassword: adminPwd });
      const token: string = r.data?.accessToken;
      if (!token) throw new Error('No token returned');
      const url = buildMerchantImpersonateUrl({
        token,
        id: loginAsTarget.id,
        email: loginAsTarget.email,
        companyName: loginAsTarget.companyName,
      });
      window.open(url, '_blank');
      toast.success(`Opening ${loginAsTarget.companyName} as merchant (new tab)`);
      setLoginAsTarget(null);
      setAdminPwd('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Impersonation failed');
    } finally {
      setLoginAsBusy(false);
    }
  };

  const openReset = (m: Merchant) => {
    setResetTarget(m);
    setNewPwd(genPassword());
    setShowNewPwd(true);
  };

  const doReset = async () => {
    if (!resetTarget) return;
    if (!newPwd || newPwd.length < 6) return toast.error('Password must be at least 6 characters');
    setResetBusy(true);
    try {
      await api.post(`/merchants/${resetTarget.id}/reset-password`, { newPassword: newPwd });
      toast.success(`Password reset for ${resetTarget.companyName}`);
      setResetTarget(null);
      setNewPwd('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  const total = merchants?.length ?? 0;
  const pageRows = (merchants ?? []).slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Merchants</h1>
          <p className="text-slate-500 text-sm mt-1">Registered businesses using the platform</p>
        </div>
        <div className="flex items-center gap-2">
          {canModule(user, 'Merchants', 'export') && (
            <button
              onClick={() => downloadBlob(`/merchants/export?${new URLSearchParams({ ...(search ? { search } : {}), ...(status !== 'all' ? { status } : {}), ...(salesUserId ? { salesUserId } : {}), ...(csUserId ? { csUserId } : {}), ...(emailVerified ? { emailVerified } : {}) }).toString()}`, `merchants-${new Date().toISOString().slice(0,10)}.csv`)}
              className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2">
              <Download size={18} /> Export
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowCampaign(true)}
              title="Email every unverified merchant a one-click verification link"
              className="border border-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-50 transition flex items-center gap-2">
              <MailCheck size={18} /> Verify Emails
            </button>
          )}
          <button
            onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
            <Plus size={18} /> Add Merchant
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        <button onClick={() => { setTab('all'); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'all' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
          All Merchants
        </button>
        <button onClick={() => { setTab('pending'); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-2 ${tab === 'pending' ? 'border-primary text-primary' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
          <Clock size={14} /> Pending Approvals
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {tab === 'all' && (
          <div className="p-4 border-b border-slate-200 flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <Search size={16} className="text-slate-400" />
              <input type="text" placeholder="Search company, brand, email, mobile..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
              <option value="all">All statuses</option>
              <option value="APPROVED">Approved</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING">Pending</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="REJECTED">Rejected</option>
            </select>
            <select value={emailVerified} onChange={(e) => { setEmailVerified(e.target.value); setPage(1); }}
              title="Filter by email verification"
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary">
              <option value="">Email: any</option>
              <option value="true">Email verified</option>
              <option value="false">Email NOT verified</option>
            </select>
            {/* Ownership filters — the basis for sales / support reporting */}
            <select value={salesUserId} onChange={(e) => { setSalesUserId(e.target.value); setPage(1); }}
              title="Filter by assigned sales person"
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary max-w-[190px]">
              <option value="">All sales persons</option>
              <option value="-1">— Unassigned —</option>
              {(assignableUsers ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={csUserId} onChange={(e) => { setCsUserId(e.target.value); setPage(1); }}
              title="Filter by assigned CS person"
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-primary max-w-[190px]">
              <option value="">All CS persons</option>
              <option value="-1">— Unassigned —</option>
              {(assignableUsers ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}

        <table className="w-full">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Verified</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Sales / CS</th>
              <th className="text-left px-4 py-3">Wallet</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Registered</th>
              <th className="text-left px-4 py-3">Last Login</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={10} className="text-center py-12 text-slate-400">Loading...</td></tr>)}
            {!isLoading && total === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400">
                <Building2 size={24} className="mx-auto mb-2 opacity-50" />
                {tab === 'pending' ? 'No pending approvals' : 'No merchants yet'}
              </td></tr>
            )}
            {pageRows.map((m) => (
              <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/merchants/${m.id}`)}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{m.companyName}</div>
                  {m.brandName && <div className="text-xs text-slate-500">{m.brandName}</div>}
                  <div className="text-[10px] text-slate-400 font-mono">ID #{m.id}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-slate-700 flex items-center gap-1">
                    <span className="truncate">{m.email}</span>
                    <button
                      title="Copy email"
                      className="text-slate-400 hover:text-primary shrink-0"
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(m.email); toast.success('Email copied'); }}
                    ><Copy size={12} /></button>
                  </div>
                  <div className="text-xs text-slate-500">{m.mobile}</div>
                </td>
                {/* Mobile always reads No for now — the SMS OTP round is a later phase, and the
                    column is here from the start so the gap is visible rather than invisible. */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5">Email <YesNo value={m.emailVerified} /></div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">Mobile <YesNo value={m.mobileVerified} /></div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{m.serviceType === 'BOTH' ? (
                    <span className="space-x-1"><span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">B2C</span><span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">B2B</span></span>
                  ) : (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${m.serviceType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>{m.serviceType}</span>
                  )}</td>
                <td className="px-4 py-3 text-xs">
                  <div className={m.salesUserName ? 'text-slate-700' : 'text-slate-400 italic'}>
                    <span className="text-slate-400">S:</span> {m.salesUserName ?? 'Unassigned'}
                  </div>
                  <div className={m.csUserName ? 'text-slate-700' : 'text-slate-400 italic'}>
                    <span className="text-slate-400">CS:</span> {m.csUserName ?? 'Unassigned'}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm tabular-nums">
                  ₹{Number(m.walletBalance ?? 0).toLocaleString('en-IN')}
                  <span className="text-xs text-slate-400 ml-1">({m.billingMode})</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusStyles[m.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {m.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs whitespace-nowrap text-slate-600">{fmtRegistered(m.createdAt)}</td>
                <td className={`px-4 py-3 text-xs whitespace-nowrap ${m.lastLoginAt ? 'text-slate-600' : 'text-slate-400 italic'}`}>{fmtLastLogin(m.lastLoginAt)}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setLoginAsTarget(m)}
                      title="Login as this merchant (admin password required)"
                      className="p-1.5 text-primary hover:bg-primary/10 rounded inline-flex items-center gap-1 text-xs font-medium">
                      <LogIn size={14} /> Login as
                    </button>
                    {isAdmin && !m.emailVerified && (
                      <button onClick={() => resendVerify.mutate(m.id)} disabled={resendVerify.isPending}
                        title="Email this merchant a verification link"
                        className="p-1.5 text-slate-500 hover:bg-slate-100 rounded inline-flex items-center gap-1 text-xs font-medium">
                        <Send size={14} /> Verify email
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => openReset(m)}
                        title="Reset this merchant's password"
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded inline-flex items-center gap-1 text-xs font-medium">
                        <KeyRound size={14} /> Reset password
                      </button>
                    )}
                    {tab === 'pending' && (
                      <>
                        <button onClick={() => approve.mutate(m.id)} disabled={approve.isPending}
                          className="p-1.5 text-success hover:bg-success/10 rounded" title="Approve">
                          <CheckCircle size={16} />
                        </button>
                        <button onClick={() => {
                          const reason = prompt('Rejection reason?');
                          if (reason) reject.mutate({ id: m.id, reason });
                        }}
                          className="p-1.5 text-danger hover:bg-danger/10 rounded" title="Reject">
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationFooter page={page} pageSize={pageSize} total={total}
          onPage={setPage} onPageSize={setPageSize} />
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add Merchant" subtitle="Create a pre-approved merchant account" maxWidth="2xl">
        <CreateMerchantForm onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={showCampaign} onClose={() => setShowCampaign(false)}
        title="Email verification campaign" subtitle="Send existing merchants a one-click link to confirm their address" maxWidth="lg">
        <EmailVerificationCampaign onDone={() => qc.invalidateQueries({ queryKey: ['merchants'] })} />
      </Modal>

      {loginAsTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setLoginAsTarget(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-lg"><Lock size={18} /></div>
              <h3 className="text-lg font-semibold">Login as {loginAsTarget.companyName}</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">Enter your admin password to open the merchant portal in a new tab as <b>{loginAsTarget.email}</b>.</p>
            <input type="password" placeholder="your admin password" value={adminPwd} autoFocus
              onChange={(e) => setAdminPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doImpersonate()}
              className="w-full px-3 py-2 border rounded-lg outline-none focus:border-primary" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setLoginAsTarget(null); setAdminPwd(''); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={doImpersonate} disabled={loginAsBusy || !adminPwd}
                className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
                {loginAsBusy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
                Open portal
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setResetTarget(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-lg"><KeyRound size={18} /></div>
              <h3 className="text-lg font-semibold">Reset password</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Set a new login password for <b>{resetTarget.companyName}</b> (<span className="font-mono">{resetTarget.email}</span>).
              Share it with the merchant — they can change it later from their portal.
            </p>
            <label className="text-xs font-medium text-slate-700">New password</label>
            <div className="flex items-center gap-2 mt-1 px-3 py-2 border border-slate-300 rounded-lg focus-within:border-primary">
              <input type={showNewPwd ? 'text' : 'password'} value={newPwd} autoFocus
                onChange={(e) => setNewPwd(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doReset()}
                className="flex-1 outline-none text-sm font-mono" />
              <button type="button" title={showNewPwd ? 'Hide' : 'Show'} onClick={() => setShowNewPwd((v) => !v)}
                className="text-slate-400 hover:text-slate-600">
                {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button type="button" title="Generate a new password" onClick={() => setNewPwd(genPassword())}
                className="text-slate-400 hover:text-primary">
                <RefreshCw size={16} />
              </button>
              <button type="button" title="Copy password"
                onClick={() => { navigator.clipboard.writeText(newPwd); toast.success('Password copied'); }}
                className="text-slate-400 hover:text-primary">
                <Copy size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">At least 6 characters. Setting this replaces the merchant's current password immediately.</p>
            {/* "Never" means the merchant is still on the password their account was created with. */}
            <p className="text-xs mt-1">
              {(resetTarget as any).passwordChangedAt
                ? <span className="text-slate-500">Password last changed: <b className="text-slate-700">{new Date((resetTarget as any).passwordChangedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</b></span>
                : <span className="text-amber-600">Password last changed: <b>Never</b></span>}
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setResetTarget(null); setNewPwd(''); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={doReset} disabled={resetBusy || newPwd.length < 6}
                className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
                {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                Reset password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
